import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, PlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useStore } from '../contexts/StoreContext';
import { Vehicle } from '../types/catalog';
import { getQuickQuote } from '../utils/finance';
import { formatARS, formatPct } from '../utils/format';
import { toDirectImageUrl } from '../utils/imageUrl';
import { images } from '../data/images';
import { AvailabilityBadge } from './AvailabilityBadge';
import { OfferStatus } from './OfferStatus';
import { WhatsAppButton } from './WhatsAppButton';
export function VehicleCard({
  vehicle
}: {
  vehicle: Vehicle;
}) {
  const {
    brands,
    compareIds,
    toggleCompare
  } = useStore();
  const brand = brands.find(b => b.id === vehicle.brandId);
  const quote = useMemo(() => getQuickQuote(vehicle, brands), [vehicle, brands]);
  const inCompare = compareIds.includes(vehicle.id);
  const onCompare = () => {
    const r = toggleCompare(vehicle.id);
    if (r === 'full') toast.error('Podés comparar hasta 3 vehículos a la vez.');else if (r === 'added') toast.success(`${vehicle.model} agregado al comparador`);
  };
  return <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-surface transition-[transform,border-color,box-shadow] duration-200 ease-out hover:-translate-y-1 hover:border-neon/60 hover:shadow-[0_10px_40px_-10px_rgba(20,99,255,0.55)]" data-id="element-1139" data-mp="2zol8ro9vbfz">
      <div className="relative" data-id="element-1140" data-mp="3bbaxbalxecds">
        <Link to={`/vehiculo/${vehicle.id}`} className="block aspect-[4/3] overflow-hidden bg-panel" aria-label={`Ver detalle de ${brand?.name} ${vehicle.model}`} data-id="element-1141" data-mp="3g8b5ps68zr82">
          <img src={toDirectImageUrl(vehicle.images[0] ?? '') || images.hero} alt={`${brand?.name ?? ''} ${vehicle.model} ${vehicle.version}`} loading="lazy" referrerPolicy="no-referrer" onError={e => {
          const img = e.currentTarget;
          if (img.src !== images.hero) img.src = images.hero;
        }} className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-1142" data-mp="3l6cosd3obqjj" />
        </Link>
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5" data-id="element-1143" data-mp="3ak2t0kz7s3n3">
          <AvailabilityBadge value={vehicle.availability} data-id="element-1144" data-mp="3ai8h1w5x7lv5" />
          {vehicle.source !== 'planilla' && <span className="inline-flex items-center rounded-full border border-white/20 bg-ink/75 px-2.5 py-1 text-[11px] font-semibold text-silver backdrop-blur" data-id="element-1145" data-mp="36gwpvm7eesku">
              Ejemplo
            </span>}
        </div>
        <button type="button" onClick={onCompare} aria-pressed={inCompare} className={`absolute right-3 top-3 inline-flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-semibold backdrop-blur transition-colors duration-150 ${inCompare ? 'border-neon bg-neon text-white' : 'border-white/20 bg-ink/70 text-white hover:border-neon'}`} data-id="element-1146" data-mp="2rm0b0q5336ys">
          {inCompare ? <CheckIcon className="h-3.5 w-3.5" data-id="element-1147" data-mp="1kzdrvtc68dur" /> : <PlusIcon className="h-3.5 w-3.5" data-id="element-1148" data-mp="1kycj5vimo3g7" />}
          Comparar
        </button>
      </div>

      <div className="flex flex-1 flex-col p-5" data-id="element-1149" data-mp="1prou52udwbqh">
        <p className="text-xs font-medium text-mist" data-id="element-1150" data-mp="1b1zpgxn31plg">
          {brand?.name} · {vehicle.bodyType} · Modelo {vehicle.year}
        </p>
        <h3 className="mt-1 font-display text-xl font-bold leading-tight" data-id="element-1151" data-mp="1g1c8t8eqweja">{vehicle.model}</h3>
        <p className="truncate text-sm text-mist" data-id="element-1152" data-mp="23rgfjon8cuei">{vehicle.version}</p>

        <div className="mt-4" data-id="element-1153" data-mp="28pro7w1sgyv5">
          <p className="text-[11px] text-mist" data-id="element-1154" data-mp="28nwzs7rb6omb">Precio de contado</p>
          <p className="font-display text-2xl font-bold tracking-tight" data-id="element-1155" data-mp="1tsh6w62gsyri">{formatARS(vehicle.price)}</p>
        </div>

        {quote && quote.sim ? <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-line pt-4 text-sm" data-id="element-1156" data-mp="1yp7m6r6g3qbv">
            <div data-id="element-1157" data-mp="iewugfhm7wzy">
              <dt className="text-[11px] text-mist" data-id="element-1158" data-mp="nbvn0tlmrvw1">Anticipo desde</dt>
              <dd className="font-semibold" data-id="element-1159" data-mp="nd6pmq3caxlk">{formatARS(quote.down)}</dd>
            </div>
            <div data-id="element-1160" data-mp="13kl0xqmkaz6t">
              <dt className="text-[11px] text-mist" data-id="element-1161" data-mp="13l4ih68avlkb">Capital a financiar</dt>
              <dd className="font-semibold" data-id="element-1162" data-mp="yrjkhe6pirl4">{formatARS(quote.principal)}</dd>
            </div>
            <div data-id="element-1163" data-mp="tu068ea0eet6">
              <dt className="text-[11px] text-mist" data-id="element-1164" data-mp="tthxwja2oa4w">{quote.ctx.offer.amortization === 'aleman' ? '1ª cuota estimada' : 'Cuota estimada'}</dt>
              <dd className="font-semibold text-neon" data-id="element-1165" data-mp="1mhm6bck131ba">
                {quote.term} × {formatARS(quote.sim.firstInstallment)}
              </dd>
            </div>
            <div data-id="element-1166" data-mp="1hjkrj3tmxo5m">
              <dt className="text-[11px] text-mist" data-id="element-1167" data-mp="1hgyeionz6gg0">TNA</dt>
              <dd className="font-semibold" data-id="element-1168" data-mp="1ci52z1s90zih">{formatPct(quote.ctx.offer.tna)}</dd>
            </div>
          </dl> : <p className="mt-4 border-t border-line pt-4 text-sm text-mist" data-id="element-1169" data-mp="17mpr2fdsxdop">Financiación a confirmar con un asesor.</p>}

        {quote && <div className="mt-3" data-id="element-1170" data-mp="3saksdz5kc9nc">
            <OfferStatus offer={quote.ctx.offer} source={quote.ctx.source} compact data-id="element-1171" data-mp="3s9j60yj3k0qb" />
          </div>}

        <div className="mt-auto grid grid-cols-[1fr_1fr_auto] gap-2 pt-5" data-id="element-1172" data-mp="27qqkdrkcj089">
          <Link to={`/vehiculo/${vehicle.id}`} className="btn btn-ghost px-2" data-id="element-1173" data-mp="2csfwlajb1jxx">
            Ver detalle
          </Link>
          <Link to={`/simulador?v=${vehicle.id}`} className="btn btn-primary px-2" data-id="element-1174" data-mp="2howkc3mldwah">
            Simular
          </Link>
          <WhatsAppButton vehicle={vehicle} iconOnly data-id="element-1175" data-mp="2rgswgilijmxp" />
        </div>
      </div>
    </article>;
}